import { useState } from "react";
import type { ConsoleEntry, NetworkEntry, ReportSuccess, TargetInfo } from "../core/types";
import type { ScreenshotResult } from "../core/screenshot";
import { CheckIcon, CloseIcon, TabBar, type PanelTab } from "./chrome";

interface PanelProps {
  target: TargetInfo | null;
  screenshot: ScreenshotResult | null;
  screenshotFailed: boolean;
  includeScreenshot: boolean;
  onIncludeScreenshotChange: (v: boolean) => void;
  onRetakeScreenshot: () => void;
  onPickTarget: () => void;
  onClearTarget: () => void;
  consoleLogs: ConsoleEntry[];
  networkLogs: NetworkEntry[];
  title: string;
  onTitleChange: (v: string) => void;
  description: string;
  onDescriptionChange: (v: string) => void;
  reporterName: string;
  onReporterNameChange: (v: string) => void;
  reporterEmail: string;
  onReporterEmailChange: (v: string) => void;
  reporterLocked: boolean;
  sending: boolean;
  error: string | null;
  success: ReporterSuccessView | null;
  badgeCount: number;
  onSwitchTab: (tab: PanelTab) => void;
  onSubmit: () => void;
  onClose: () => void;
}

export interface ReporterSuccessView {
  bug: ReportSuccess;
  screenshotDropped: boolean;
}

function CameraIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
      <circle cx="12" cy="13" r="3" />
    </svg>
  );
}

function TargetIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  );
}

export function Panel(props: PanelProps) {
  const [showDetail, setShowDetail] = useState(false);
  const { success } = props;

  if (success) {
    return (
      <div className="bw-panel" role="dialog" aria-label="Bug signalé">
        <div className="bw-panel-header">
          <span className="bw-panel-title">Signaler un bug</span>
          <button type="button" className="bw-close" onClick={props.onClose} aria-label="Fermer">
            <CloseIcon />
          </button>
        </div>
        <div className="bw-panel-body">
          <div className="bw-success">
            <div className="bw-success-icon">
              <CheckIcon />
            </div>
            <div className="bw-success-title">Bug signalé ✓</div>
            <p style={{ color: "var(--bw-muted)", fontSize: 13 }}>
              Merci ! Votre signalement a bien été transmis à l’équipe.
              {success.screenshotDropped && " (envoyé sans la capture d’écran, trop volumineuse)"}
            </p>
          </div>
        </div>
        <div className="bw-panel-footer" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <button type="button" className="bw-btn" onClick={() => props.onSwitchTab("tracking")}>
            Voir mes signalements
          </button>
          <button type="button" className="bw-btn bw-btn-ghost" onClick={props.onClose}>
            Fermer
          </button>
        </div>
      </div>
    );
  }

  const attachParts: string[] = [];
  if (props.includeScreenshot && props.screenshot) attachParts.push("capture");
  attachParts.push(`${props.consoleLogs.length} logs`);
  attachParts.push(`${props.networkLogs.length} requêtes`);

  const canSubmit = props.description.trim().length > 0 && !props.sending;

  return (
    <div className="bw-panel" role="dialog" aria-label="Signaler un bug">
      <div className="bw-panel-header">
        <span className="bw-panel-title">Signaler un bug</span>
        <button type="button" className="bw-close" onClick={props.onClose} aria-label="Fermer">
          <CloseIcon />
        </button>
      </div>

      <TabBar active="report" onChange={props.onSwitchTab} badgeCount={props.badgeCount} />

      <div className="bw-panel-body">
        {props.screenshot ? (
          <div className="bw-field">
            <div className={"bw-shot" + (props.includeScreenshot ? "" : " bw-shot-off")}>
              <img src={props.screenshot.dataUrl} alt="Capture d’écran de la page" />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <label className="bw-check">
                <input
                  type="checkbox"
                  checked={props.includeScreenshot}
                  onChange={(e) => props.onIncludeScreenshotChange(e.target.checked)}
                />
                Joindre la capture d’écran
              </label>
              <button type="button" className="bw-link-btn" onClick={props.onRetakeScreenshot}>
                Reprendre
              </button>
            </div>
          </div>
        ) : props.screenshotFailed ? (
          <p className="bw-shot-note">
            La capture d’écran n’a pas pu être générée — le signalement sera envoyé sans image.
          </p>
        ) : (
          <button type="button" className="bw-target-add" onClick={props.onRetakeScreenshot}>
            <CameraIcon />
            Ajouter une capture d’écran
          </button>
        )}

        {props.target ? (
          <div className="bw-target-chip">
            <span>Élément :</span>
            <code title={props.target.selector}>
              {props.target.componentName ?? props.target.selector}
            </code>
            <button type="button" className="bw-link-btn" onClick={props.onPickTarget}>
              Changer
            </button>
            <button type="button" className="bw-link-btn bw-link-btn-muted" onClick={props.onClearTarget}>
              Retirer
            </button>
          </div>
        ) : (
          <button type="button" className="bw-target-add" onClick={props.onPickTarget}>
            <TargetIcon />
            Sélectionner l’élément concerné <span className="bw-optional">(optionnel)</span>
          </button>
        )}

        <div className="bw-field">
          <label className="bw-label" htmlFor="bw-title">
            Titre <span className="bw-optional">(optionnel)</span>
          </label>
          <input
            id="bw-title"
            className="bw-input"
            value={props.title}
            onChange={(e) => props.onTitleChange(e.target.value)}
            placeholder="Résumé en une ligne"
          />
        </div>

        <div className="bw-field">
          <label className="bw-label" htmlFor="bw-description">
            Description
          </label>
          <textarea
            id="bw-description"
            className="bw-textarea"
            value={props.description}
            onChange={(e) => props.onDescriptionChange(e.target.value)}
            placeholder="Que s’est-il passé ? Qu’attendiez-vous à la place ?"
          />
        </div>

        {!props.reporterLocked && (
          <div className="bw-row">
            <div className="bw-field">
              <label className="bw-label" htmlFor="bw-name">
                Nom <span className="bw-optional">(optionnel)</span>
              </label>
              <input
                id="bw-name"
                className="bw-input"
                value={props.reporterName}
                onChange={(e) => props.onReporterNameChange(e.target.value)}
              />
            </div>
            <div className="bw-field">
              <label className="bw-label" htmlFor="bw-email">
                Email <span className="bw-optional">(optionnel)</span>
              </label>
              <input
                id="bw-email"
                className="bw-input"
                type="email"
                value={props.reporterEmail}
                onChange={(e) => props.onReporterEmailChange(e.target.value)}
              />
            </div>
          </div>
        )}

        <div className="bw-attach">
          <div className="bw-attach-summary">
            <span>Sera joint : {attachParts.join(", ")}</span>
            <button type="button" className="bw-link-btn" onClick={() => setShowDetail((v) => !v)}>
              {showDetail ? "Masquer" : "Détail"}
            </button>
          </div>
          {showDetail && (
            <div className="bw-attach-detail">
              <ul>
                {props.consoleLogs.map((log, i) => (
                  <li key={"c" + i} className={log.level === "error" ? "bw-log-error" : log.level === "warn" ? "bw-log-warn" : ""} title={log.message}>
                    [{log.level}] {log.message}
                  </li>
                ))}
                {props.networkLogs.map((req, i) => (
                  <li key={"n" + i} className={req.status === null || req.status >= 400 ? "bw-log-error" : ""} title={req.url}>
                    {req.method} {req.status ?? "ERR"} {req.url}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {props.error && <div className="bw-error">{props.error}</div>}
      </div>

      <div className="bw-panel-footer">
        <button type="button" className="bw-btn" disabled={!canSubmit} onClick={props.onSubmit}>
          {props.sending ? (
            <>
              <span className="bw-spinner" /> Envoi en cours…
            </>
          ) : props.error ? (
            "Réessayer"
          ) : (
            "Envoyer le signalement"
          )}
        </button>
      </div>
    </div>
  );
}
